import React, { useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Alert, CircularProgress, useTheme } from '@mui/material';
import config from '../config';
import AvailabilityPicker, { AvailabilitySlot as Slot } from './AvailabilityPicker';

type Audience = 'provider' | 'tenant';

interface Props {
  reminderId: string;
  expiryDate?: string;
  responses: any[];
  onChanged: () => void;
}

const slotLabel = (s: Slot) =>
  `${new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${s.start_time}–${s.end_time}`;

// Shown in the flow when "Contacts providers & tenants" is off: the landlord reaches the parties
// themselves and enters when each is free. The surrounding step explains what happens next.
const ManualBookingBoxes: React.FC<Props> = ({ reminderId, expiryDate, responses, onChanged }) => {
  const { palette } = useTheme();
  const t = palette.tokens;
  const [editing, setEditing] = useState<Audience | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const responseFor = (audience: Audience) => responses.find(r => r.audience === audience && r.manual);

  const open = (audience: Audience) => {
    const existing = responseFor(audience);
    setSlots(existing?.submitted_slots?.length ? existing.submitted_slots : []);
    setNotes(existing?.notes || '');
    setError(null);
    setEditing(audience);
  };

  const save = async () => {
    if (!editing || slots.length === 0) { setError('Pick at least one date.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/reminders/${reminderId}/manual-availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ audience: editing, slots, notes: notes.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save');
      setEditing(null);
      onChanged();
    } catch (e: any) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const row = (audience: Audience) => {
    const response = responseFor(audience);
    const isProvider = audience === 'provider';
    return (
      <Box key={audience} sx={{ bgcolor: t.surfaceCard, border: `1px solid ${t.hairline}`, borderRadius: '8px', p: 0.75, minWidth: 200 }} onClick={e => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Chip label={isProvider ? 'Provider' : 'Tenant'} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: t.primary, color: t.onPrimary }} />
          <Typography sx={{ fontSize: '0.7rem', color: t.ink, fontWeight: 600 }}>
            {response ? response.who : 'Availability'}
          </Typography>
        </Box>
        {response ? (
          <Box sx={{ mb: 0.5 }}>
            {response.submitted_slots.map((s: Slot, i: number) => (
              <Typography key={i} sx={{ fontSize: '0.68rem', color: t.muted }}>{slotLabel(s)}</Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.68rem', color: t.muted, mb: 0.5 }}>Not entered yet</Typography>
        )}
        <Typography
          onClick={e => { e.stopPropagation(); open(audience); }}
          sx={{ fontSize: '0.68rem', color: t.primary, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
        >
          {response ? 'Edit availability' : 'Enter availability'}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }} onClick={e => e.stopPropagation()}>
      {row('provider')}
      {row('tenant')}

      <Dialog open={!!editing} onClose={saving ? undefined : () => setEditing(null)} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
        <DialogTitle>{editing === 'provider' ? 'Provider' : 'Tenant'} availability</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            When can the {editing} do the visit? Pick every date that works and set the times.
          </Typography>
          <AvailabilityPicker
            slots={slots} onSlotsChange={setSlots} notes={notes} onNotesChange={setNotes}
            expiryDate={expiryDate ? new Date(expiryDate + 'T00:00:00') : null}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualBookingBoxes;
